import { Link } from 'react-router-dom';
import { useState } from 'react';
import { 
  ArrowRight, CheckCircle, Clock, Users, Award, BookOpen, 
  Wrench, Cog, Building2, Briefcase, GraduationCap, Play,
  Monitor, FileText, Settings, Globe, Shield, TrendingUp, Code
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { createLead } from '@/lib/api';

// Odoo Certification Training Paths
const trainingPaths = [
  {
    id: 'functional',
    name: 'Odoo Functional Certification',
    level: 'Beginner to Intermediate',
    duration: '8-10 weeks',
    description: 'Master Odoo business applications configuration including Sales, CRM, Inventory, Purchase, and Accounting modules for end-to-end business process management.',
    color: 'from-purple-500 to-purple-700',
    icon: '📊',
    topics: [
      'Odoo Architecture & Navigation',
      'Sales & CRM Configuration',
      'Inventory & Warehouse Management',
      'Purchase & Procurement',
      'Accounting & Invoicing',
      'Manufacturing (MRP)',
      'Human Resources',
      'Reporting & Dashboards'
    ],
    outcomes: [
      'Configure Odoo modules for business needs',
      'Implement end-to-end business processes',
      'Create custom reports and dashboards',
      'Manage multi-company setups',
      'Prepare for Odoo Functional Certification exam'
    ],
    salary: '$65,000 - $100,000',
    demand: 'Very High',
    industries: ['SMBs', 'E-commerce', 'Manufacturing', 'Services']
  },
  {
    id: 'technical',
    name: 'Odoo Technical Certification',
    level: 'Intermediate to Advanced',
    duration: '10-12 weeks',
    description: 'Become an Odoo developer with expertise in Python, ORM, QWeb templates, and custom module development for enterprise customizations.',
    color: 'from-indigo-500 to-indigo-700',
    icon: '💻',
    topics: [
      'Python for Odoo Development',
      'Odoo ORM & Models',
      'Views & QWeb Templates',
      'Security & Access Rights',
      'API & Web Services',
      'Custom Module Development',
      'Odoo.sh & Deployment',
      'Performance Optimization'
    ],
    outcomes: [
      'Develop custom Odoo modules',
      'Build integrations with external systems',
      'Customize existing Odoo functionality',
      'Deploy and maintain Odoo instances',
      'Prepare for Odoo Technical Certification exam'
    ],
    salary: '$75,000 - $120,000',
    demand: 'Very High',
    industries: ['Tech Companies', 'ERP Consultancies', 'Software Houses']
  },
  {
    id: 'accounting',
    name: 'Odoo Accounting Specialist',
    level: 'Intermediate',
    duration: '6-8 weeks',
    description: 'Specialize in Odoo Accounting for financial management, multi-currency operations, tax compliance, and financial reporting.',
    color: 'from-emerald-500 to-emerald-700',
    icon: '💰',
    topics: [
      'Chart of Accounts Setup',
      'Accounts Payable & Receivable',
      'Bank Reconciliation',
      'Multi-Currency Accounting',
      'Tax Management & Compliance',
      'Financial Statements',
      'Budget Management',
      'Audit Trail & Controls'
    ],
    outcomes: [
      'Configure Odoo Accounting for any business',
      'Manage complete financial workflows',
      'Handle multi-currency transactions',
      'Generate statutory financial reports',
      'Implement financial controls'
    ],
    salary: '$70,000 - $105,000',
    demand: 'High',
    industries: ['Financial Services', 'Retail', 'Manufacturing']
  },
  {
    id: 'ecommerce',
    name: 'Odoo E-commerce & Website',
    level: 'Beginner to Intermediate',
    duration: '6-8 weeks',
    description: 'Master Odoo Website Builder and E-commerce for creating stunning online stores with integrated inventory, payments, and shipping.',
    color: 'from-pink-500 to-pink-700',
    icon: '🛒',
    topics: [
      'Odoo Website Builder',
      'E-commerce Configuration',
      'Product Catalog Management',
      'Payment Gateway Integration',
      'Shipping & Delivery',
      'SEO & Marketing Tools',
      'Customer Portal',
      'Analytics & Conversion'
    ],
    outcomes: [
      'Build professional e-commerce websites',
      'Configure payment and shipping methods',
      'Integrate with inventory management',
      'Implement marketing automation',
      'Optimize for conversions'
    ],
    salary: '$60,000 - $95,000',
    demand: 'High',
    industries: ['E-commerce', 'Retail', 'D2C Brands']
  }
];

// Odoo Modules
const odooModules = [
  { id: 'sales-crm', name: 'Sales & CRM', icon: TrendingUp, duration: '2 weeks', topics: ['Lead Management', 'Opportunity Pipeline', 'Quotations & Orders', 'Sales Teams', 'Reporting'] },
  { id: 'inventory', name: 'Inventory & Warehouse', icon: Building2, duration: '2 weeks', topics: ['Stock Management', 'Warehouse Operations', 'Barcode Scanning', 'Routes & Rules', 'Inventory Valuation'] },
  { id: 'accounting', name: 'Accounting & Finance', icon: FileText, duration: '2 weeks', topics: ['Chart of Accounts', 'AP/AR', 'Bank Reconciliation', 'Tax Management', 'Financial Reports'] },
  { id: 'manufacturing', name: 'Manufacturing (MRP)', icon: Cog, duration: '2 weeks', topics: ['Bill of Materials', 'Work Orders', 'Work Centers', 'Planning', 'Quality Control'] },
  { id: 'purchase', name: 'Purchase & Procurement', icon: Globe, duration: '1.5 weeks', topics: ['Vendor Management', 'RFQs & POs', 'Procurement Rules', 'Vendor Bills', 'Reporting'] },
  { id: 'hr', name: 'Human Resources', icon: Users, duration: '1.5 weeks', topics: ['Employee Management', 'Recruitment', 'Time Off', 'Payroll', 'Appraisals'] }
];

// Corporate clients using Odoo
const corporateClients = ['Toyota', 'Hyundai', 'WWF', 'Danone', 'TED', 'Base Camp'];

// Industries
const industries = [
  { name: 'E-commerce & Retail', icon: Globe },
  { name: 'Manufacturing', icon: Cog },
  { name: 'Services', icon: Briefcase },
  { name: 'Wholesale Distribution', icon: Building2 },
  { name: 'Healthcare', icon: Shield },
  { name: 'Education', icon: GraduationCap }
];

export default function OdooDetailPage() {
  const [selectedPath, setSelectedPath] = useState('functional');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({
        first_name: 'Odoo Demo',
        last_name: 'Request',
        email,
        message: 'Interested in Odoo ERP Training - Demo Request',
        source: 'odoo_demo'
      });
      toast.success('Demo request submitted! We\'ll contact you within 24 hours.');
      setEmail('');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="odoo-detail-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-purple-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/programs" className="inline-flex items-center text-sm text-purple-600 hover:text-purple-700 mb-6">
                <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                Back to Programs
              </Link>
              
              <Badge className="bg-purple-100 text-purple-700 mb-4">World's #1 Open Source ERP</Badge>
              
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-3xl mb-6">
                🔧
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
                Odoo ERP Training & Certification
              </h1>
              <p className="text-xl text-purple-600 font-medium mb-4">
                Open-Source ERP for Modern Business
              </p>
              <p className="text-lg text-slate-600 mb-8">
                Odoo is the most installed business software in the world with 12+ million users. 
                Master this versatile, modular ERP system covering CRM, Sales, Inventory, Manufacturing, 
                Accounting, E-commerce, and 30+ integrated business applications.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/pricing">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6" data-testid="odoo-enroll-btn">
                    Enroll Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/webinar">
                  <Button variant="outline" className="rounded-full px-6 border-purple-200 text-purple-700 hover:bg-purple-50">
                    <Play className="w-4 h-4 mr-2" /> Free Demo Session
                  </Button>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-purple-600">12M+</p>
                  <p className="text-xs text-slate-500">Global Users</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-purple-600">30+</p>
                  <p className="text-xs text-slate-500">Integrated Apps</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-purple-600">92%</p>
                  <p className="text-xs text-slate-500">Placement Rate</p>
                </div>
              </div>
            </div>
            
            {/* Program Highlights Card */}
            <Card className="shadow-xl border-0 bg-white">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  Program Highlights
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Flexible Duration</p>
                    <p className="text-sm text-slate-600">6-12 weeks based on track</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Live Online Training</p>
                    <p className="text-sm text-slate-600">Expert-led via Zoom/Teams</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Code className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Hands-On Labs</p>
                    <p className="text-sm text-slate-600">Real Odoo instance access</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Official Certification</p>
                    <p className="text-sm text-slate-600">Odoo certification exam prep</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Job Assistance</p>
                    <p className="text-sm text-slate-600">Resume & interview prep</p>
                  </div>
                </div>
                
                {/* Demo Request Form */}
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700 mb-2">Request Free Demo</p>
                  <form onSubmit={handleDemoRequest} className="flex gap-2">
                    <Input 
                      type="email" 
                      placeholder="Enter your email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1"
                      data-testid="odoo-demo-email"
                    />
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={loading}>
                      {loading ? '...' : 'Go'}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Industries Using Odoo</p>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry) => (
              <div key={industry.name} className="flex items-center px-4 py-2 bg-slate-50 rounded-full text-sm">
                <industry.icon className="w-4 h-4 text-purple-600 mr-2" />
                <span className="text-slate-700">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Odoo Certification Paths */}
      <section className="py-20 lg:py-28" id="certifications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-purple-100 text-purple-700 mb-4">Official Odoo Certifications</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Choose Your Odoo Certification Path</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Enroll in specific Odoo certification training aligned with your career goals
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {trainingPaths.map((path) => (
              <Card 
                key={path.id} 
                className={`relative overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                  selectedPath === path.id ? 'border-purple-500 shadow-xl ring-2 ring-purple-200' : 'border-slate-200 hover:border-purple-300 hover:shadow-lg'
                }`}
                onClick={() => setSelectedPath(path.id)}
                data-testid={`cert-path-${path.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${path.color}`} />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{path.icon}</span>
                      <div>
                        <CardTitle className="text-xl text-slate-900">{path.name}</CardTitle>
                        <p className="text-sm text-slate-500">{path.level} • {path.duration}</p>
                      </div>
                    </div>
                    <Badge className={`${path.demand === 'Very High' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'}`}>
                      {path.demand} Demand
                    </Badge>
                  </div>
                  <CardDescription className="text-slate-600">{path.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Industries */}
                    <div className="flex flex-wrap gap-2">
                      {path.industries.map((ind, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">{ind}</span>
                      ))}
                    </div>
                    
                    {/* Salary */}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-slate-600">Expected Salary:</span>
                      <span className="font-bold text-green-700">{path.salary}</span>
                    </div>
                    
                    {/* Topics */}
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Key Topics:</p>
                      <div className="grid grid-cols-2 gap-1">
                        {path.topics.slice(0, 6).map((topic, i) => (
                          <div key={i} className="flex items-center text-xs text-slate-600">
                            <CheckCircle className="w-3 h-3 text-purple-500 mr-1 flex-shrink-0" />
                            <span className="truncate">{topic}</span>
                          </div>
                        ))}
                      </div>
                      {path.topics.length > 6 && (
                        <p className="text-xs text-purple-600 mt-1">+{path.topics.length - 6} more topics</p>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex gap-3 pt-2">
                      <Link to="/pricing" className="flex-1">
                        <Button className={`w-full bg-gradient-to-r ${path.color} text-white hover:opacity-90`}>
                          Enroll Now
                        </Button>
                      </Link>
                      <Link to="/webinar">
                        <Button variant="outline" className="border-slate-300">
                          <Play className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Curriculum */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Odoo Training Curriculum</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive modules covering all essential Odoo functionalities
            </p>
          </div>
          
          <Tabs defaultValue="sales-crm" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-10 bg-transparent">
              {odooModules.slice(0, 4).map((module) => (
                <TabsTrigger 
                  key={module.id} 
                  value={module.id}
                  className="px-4 py-2 rounded-full border border-slate-200 data-[state=active]:border-purple-500 data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700"
                >
                  {module.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {odooModules.map((module) => (
              <TabsContent key={module.id} value={module.id}>
                <Card className="max-w-4xl mx-auto">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <module.icon className="w-6 h-6 text-purple-700" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">{module.name}</h3>
                        <p className="text-purple-600 font-medium">Duration: {module.duration}</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">Topics Covered</h4>
                        <ul className="space-y-3">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-600">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">What You'll Learn</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start space-x-2">
                            <BookOpen className="w-5 h-5 text-purple-500 mt-0.5" />
                            <span className="text-slate-600">Real-time scenario-based training</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Monitor className="w-5 h-5 text-purple-500 mt-0.5" />
                            <span className="text-slate-600">Hands-on lab exercises</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <FileText className="w-5 h-5 text-purple-500 mt-0.5" />
                            <span className="text-slate-600">Study materials & documentation</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Award className="w-5 h-5 text-purple-500 mt-0.5" />
                            <span className="text-slate-600">Certification guidance</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          {/* All Modules Grid */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">All Odoo Modules</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {odooModules.map((module, index) => (
                <Card key={module.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <span className="text-purple-700 font-mono font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{module.name}</p>
                        <p className="text-xs text-slate-500">{module.duration}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Outcomes */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Odoo Certifications We Prepare You For</h2>
              <p className="text-slate-600 mb-6">
                Our training programs prepare you for official Odoo S.A. certifications.
              </p>
              <ul className="space-y-3">
                {[
                  { cert: 'Odoo Functional Certification', color: 'text-purple-600' },
                  { cert: 'Odoo Technical Certification', color: 'text-indigo-600' },
                  { cert: 'Odoo Accounting Specialist', color: 'text-emerald-600' },
                  { cert: 'Odoo Website/E-commerce Certification', color: 'text-pink-600' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Award className={`w-5 h-5 ${item.color} mt-0.5`} />
                    <span className="text-slate-700 font-medium">{item.cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Career Opportunities</h2>
              <p className="text-slate-600 mb-6">
                Odoo skills are in high demand globally. Here are roles you can pursue.
              </p>
              <ul className="space-y-3">
                {[
                  'Odoo Functional Consultant',
                  'Odoo Developer',
                  'Odoo Implementation Specialist',
                  'Odoo Project Manager',
                  'Odoo Solution Architect',
                  'ERP Business Analyst',
                  'Odoo Support Engineer',
                  'Odoo Partner/Freelancer'
                ].map((role, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Briefcase className="w-5 h-5 text-purple-500 mt-0.5" />
                    <span className="text-slate-700">{role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Clients */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Companies Using Odoo</p>
          <div className="flex flex-wrap justify-center gap-6">
            {corporateClients.map((client) => (
              <div key={client} className="px-6 py-3 bg-white rounded-lg shadow-sm text-slate-700 font-medium">
                {client}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Why Choose Our Odoo Training?</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'Odoo-Certified Trainers', desc: 'Learn from officially certified Odoo consultants with real project experience' },
              { icon: Monitor, title: 'Live Online Sessions', desc: 'Interactive training via Zoom/Teams with screen sharing & Q&A' },
              { icon: Code, title: 'Hands-On Labs', desc: 'Practice on real Odoo instances with guided exercises' },
              { icon: FileText, title: 'Study Materials', desc: 'Comprehensive documentation, videos, and practice exercises' },
              { icon: Award, title: 'Certification Prep', desc: 'Guidance for official Odoo certification exams' },
              { icon: Briefcase, title: 'Career Support', desc: 'Resume building, mock interviews, and job placement assistance' }
            ].map((benefit, i) => (
              <Card key={i} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-7 h-7 text-purple-700" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-purple-600 to-purple-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Master Odoo ERP?
            </h2>
            <p className="text-lg text-purple-100 mb-8">
              Join our next batch and become a certified Odoo professional. 
              Customized training available for individuals and corporate teams.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="bg-white text-purple-700 hover:bg-purple-50 rounded-full px-8 py-3 font-medium">
                  View Pricing & Enroll
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  Request Corporate Training
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
